import { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  Users, Search, Crown, Tag, ShoppingBag, Eye, X, 
  Star, RefreshCw, Mail, Phone, MapPin, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIP_TIERS = [
  { key: '', label: 'None', color: 'bg-gray-100 text-gray-600' },
  { key: 'silver', label: 'Silver', color: 'bg-slate-200 text-slate-700' },
  { key: 'gold', label: 'Gold', color: 'bg-gold/20 text-gold' },
  { key: 'platinum', label: 'Platinum', color: 'bg-purple-100 text-purple-700' }
];

const CUSTOMER_TAGS = [
  'Frequent Buyer', 'High Value', 'New Customer', 'Loyal', 'Review Writer', 'Influencer'
];

export const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [vipFilter, setVipFilter] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [editData, setEditData] = useState({ is_vip: false, vip_tier: '', tags: [], notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCustomers();
  }, [page, vipFilter]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({ page, limit: 50 });
      if (vipFilter === 'vip') params.append('is_vip', 'true');
      if (vipFilter === 'regular') params.append('is_vip', 'false');
      
      const response = await axios.get(`${API}/admin/customers?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCustomers(response.data.customers || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const openCustomerModal = async (customer) => {
    setSelectedCustomer(customer);
    setEditData({
      is_vip: customer.is_vip || false,
      vip_tier: customer.vip_tier || '',
      tags: customer.tags || [],
      notes: customer.notes || ''
    });
    setShowModal(true);
    
    // Load customer orders
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/customers/${customer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to load customer details');
    }
  };

  const saveCustomer = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/customers/${selectedCustomer.id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Customer updated');
      setShowModal(false);
      loadCustomers();
    } catch (error) {
      toast.error('Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tag) => {
    setEditData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredCustomers = customers.filter(customer => {
    if (!searchQuery) return true;
    return customer.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           customer.full_name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const stats = {
    total: total,
    vip: customers.filter(c => c.is_vip).length,
    repeat: customers.filter(c => c.is_repeat).length
  };

  if (loading && customers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-customers">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-light text-charcoal">Customer Management</h1>
        <p className="text-charcoal-light text-sm mt-1">View and manage your customer database</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-charcoal/10 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gold/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-gold" />
            </div>
            <div>
              <p className="text-xs tracking-wider uppercase text-charcoal-light">Total Customers</p>
              <p className="font-heading text-2xl font-light text-charcoal">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-charcoal/10 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-50 flex items-center justify-center">
              <Crown className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-xs tracking-wider uppercase text-charcoal-light">VIP Customers</p>
              <p className="font-heading text-2xl font-light text-purple-600">{stats.vip}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-charcoal/10 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-50 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-xs tracking-wider uppercase text-charcoal-light">Repeat Customers</p>
              <p className="font-heading text-2xl font-light text-green-600">{stats.repeat}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-charcoal/10 p-4 flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal-light" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full h-10 pl-10 pr-4 border border-charcoal/10 focus:border-gold outline-none"
          />
        </div>
        <select
          value={vipFilter}
          onChange={(e) => setVipFilter(e.target.value)}
          className="h-10 px-4 border border-charcoal/10 focus:border-gold outline-none bg-white"
        >
          <option value="">All Customers</option>
          <option value="vip">VIP Only</option>
          <option value="regular">Regular Only</option>
        </select>
      </div>

      {/* Customers Table */}
      <div className="bg-white border border-charcoal/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-beige/50">
              <tr>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Customer</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Status</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Orders</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Total Spent</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Last Order</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Tags</th>
                <th className="text-left px-5 py-3 text-xs tracking-wider uppercase text-charcoal-light font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal/5">
              {filteredCustomers.length > 0 ? (
                filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-beige/30 transition-colors">
                    <td className="px-5 py-4">
                      <p className="text-sm font-medium text-charcoal">{customer.full_name}</p>
                      <p className="text-xs text-charcoal-light">{customer.email}</p>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        {customer.is_vip && (
                          <span className={`text-xs px-2 py-1 ${
                            VIP_TIERS.find(t => t.key === customer.vip_tier)?.color || 'bg-gold/20 text-gold'
                          }`}>
                            <Crown className="w-3 h-3 inline mr-1" />
                            {customer.vip_tier || 'VIP'}
                          </span>
                        )}
                        {customer.is_repeat && (
                          <span className="text-xs px-2 py-1 bg-green-50 text-green-700">
                            Repeat
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm text-charcoal">{customer.total_orders}</span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm font-medium text-charcoal">{formatCurrency(customer.total_spent || 0)}</span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm text-charcoal-light">{formatDate(customer.last_order_date)}</span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1">
                        {customer.tags?.slice(0, 2).map((tag, idx) => (
                          <span key={idx} className="text-xs px-2 py-0.5 bg-beige text-charcoal-light">
                            {tag}
                          </span>
                        ))}
                        {customer.tags?.length > 2 && (
                          <span className="text-xs text-charcoal-light">+{customer.tags.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => openCustomerModal(customer)}
                        className="p-1.5 text-charcoal-light hover:text-gold hover:bg-beige transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-charcoal-light">
                    No customers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-charcoal/10 flex items-center justify-between">
            <span className="text-sm text-charcoal-light">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-charcoal/20 text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 border border-charcoal/20 text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Customer Detail Modal */}
      {showModal && selectedCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50">
          <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-charcoal/10 p-6 flex items-center justify-between">
              <h2 className="font-heading text-xl font-light text-charcoal">
                Customer Profile
              </h2>
              <button onClick={() => setShowModal(false)} className="text-charcoal-light hover:text-charcoal">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Customer Info */}
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-gold flex items-center justify-center">
                  <span className="text-3xl text-white font-heading">
                    {selectedCustomer.full_name?.charAt(0) || 'C'}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-2xl font-light text-charcoal">{selectedCustomer.full_name}</h3>
                  <div className="mt-2 space-y-1 text-sm text-charcoal-light">
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> {selectedCustomer.email}
                    </p>
                    <p className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Joined {formatDate(selectedCustomer.created_at)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs tracking-wider uppercase text-charcoal-light">Total Spent</p>
                  <p className="font-heading text-3xl font-light text-gold">{formatCurrency(selectedCustomer.total_spent || 0)}</p>
                  <p className="text-sm text-charcoal-light">{selectedCustomer.total_orders} orders</p>
                </div>
              </div>

              {/* VIP Status */}
              <div>
                <h4 className="text-xs tracking-wider uppercase text-charcoal-light font-medium mb-3">VIP Status</h4>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editData.is_vip}
                      onChange={(e) => setEditData({ ...editData, is_vip: e.target.checked })}
                      className="w-5 h-5 accent-gold"
                    />
                    <span className="text-sm text-charcoal">VIP Customer</span>
                  </label>
                  
                  {editData.is_vip && (
                    <select
                      value={editData.vip_tier}
                      onChange={(e) => setEditData({ ...editData, vip_tier: e.target.value })}
                      className="h-9 px-3 border border-charcoal/10 focus:border-gold outline-none bg-white text-sm"
                    >
                      {VIP_TIERS.map(tier => (
                        <option key={tier.key} value={tier.key}>{tier.label}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Tags */}
              <div>
                <h4 className="text-xs tracking-wider uppercase text-charcoal-light font-medium mb-3">Customer Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {CUSTOMER_TAGS.map(tag => (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`px-3 py-1.5 text-sm border transition-colors ${
                        editData.tags.includes(tag)
                          ? 'border-gold bg-gold/10 text-gold'
                          : 'border-charcoal/10 text-charcoal-light hover:border-gold/50'
                      }`}
                    >
                      <Tag className="w-3 h-3 inline mr-1" />
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <h4 className="text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">Internal Notes</h4>
                <textarea
                  value={editData.notes}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  className="w-full h-24 px-4 py-2 border border-charcoal/10 focus:border-gold outline-none resize-none"
                  placeholder="Add notes about this customer..."
                />
              </div>

              {/* Order History */}
              <div>
                <h4 className="text-xs tracking-wider uppercase text-charcoal-light font-medium mb-3">Order History</h4>
                {customerOrders.length > 0 ? (
                  <div className="border border-charcoal/10 divide-y divide-charcoal/5 max-h-60 overflow-y-auto">
                    {customerOrders.map((order) => (
                      <div key={order.id} className="p-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-charcoal">#{order.order_number}</p>
                          <p className="text-xs text-charcoal-light">{formatDate(order.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-charcoal">{formatCurrency(order.total_amount)}</p>
                          <p className={`text-xs ${
                            order.order_status === 'delivered' ? 'text-green-600' : 'text-charcoal-light'
                          }`}>
                            {order.order_status}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-charcoal-light">No orders yet</p>
                )}
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-charcoal/10 p-6 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-charcoal/20 text-charcoal text-sm"
              >
                Cancel
              </button>
              <button
                onClick={saveCustomer}
                disabled={saving}
                className="px-6 py-2 bg-gold text-white text-sm disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
